import type {semantic} from '@linx-security/ui/design-tokens/light-legacy';
import type {Node as ReactflowNode} from 'reactflow';
import type {CSSProperties, JSX} from 'react';

// A list of custom defined node types
export enum NodeVariant {
  GroupedContent = `grouped-content`,
  Content = `content`,
  Minimal = `minimal`,
}

// A map of node types to their respective data
type NodeDataMap = {
  [NodeVariant.GroupedContent]: GroupedContentNodeData;
  [NodeVariant.Content]: ContentNodeData;
  [NodeVariant.Minimal]: MinimalNodeData;
};

export type NodeData<T extends NodeVariant = NodeVariant> = {
  variant?: T;
  style?: CSSProperties;
  visible?: boolean;
  type?: string;
  [key: string]: unknown;
} & NodeDataMap[T];

export type NodeWithOptionalPosition<TData extends NodeData = NodeData> = Omit<Node<TData>, 'position'> & {
  position?: Node['position'];
};

// TODO let's use the actual type from the new tokens here
export type IntentColor = keyof typeof semantic.colors.intent | string;

export type ContentNodeData = {
  label: string;
  sublabel?: string;
  icon: JSX.Element;
  // TODO: accept array of badge props or array of string
  tagline: string;
  highlight?: IntentColor;
  // TODO change highlight to badgeProps
  badgeText?: string;
  badgeColorScheme?: string;
  variant?: NodeVariant.Content;
};

export type GroupedContentNodeData = {
  children: Array<Node<any, NodeVariant>>;
  originalEdges: Array<{source: string; target: string; id: string}>;
  maxDisplayedChildren?: number;
  variant?: NodeVariant.GroupedContent;
  expandable?: boolean;
  isExpanded?: boolean;
} & Omit<ContentNodeData, 'variant'>;

type MinimalNodeData = {
  colorScheme?: string;
  label: string;
  variant?: NodeVariant.Minimal;
};

export const isMinimalNodeData = (node: NodeData | undefined): node is NodeData<NodeVariant.Minimal> => {
  return node?.variant === NodeVariant.Minimal;
};

export const isContentNodeData = (node: NodeData | undefined): node is NodeData<NodeVariant.Content> => {
  return node?.variant === NodeVariant.Content;
};

export const isGroupedContentNodeData = (data: NodeData | undefined): data is NodeData<NodeVariant.GroupedContent> => {
  return data?.variant === NodeVariant.GroupedContent;
};

export type Node<TData extends unknown = unknown, V extends NodeVariant = NodeVariant> = ReactflowNode<
  NodeData<V> & TData
>;

export const isGroupedContentNode = (node: Node | undefined): node is Node<GroupedContentNodeData> => {
  return node?.data?.variant === NodeVariant.GroupedContent;
};
